import React, { useEffect, useRef, useState } from "react";
import "./Login.css";

import {
  FiActivity,
  FiArrowLeft,
  FiBarChart2,
  FiBox,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiDatabase,
  FiEye,
  FiEyeOff,
  FiEdit3,
  FiKey,
  FiLock,
  FiMail,
  FiMonitor,
  FiPackage,
  FiServer,
  FiShield,
  FiSmartphone,
  FiTool,
  FiTrendingUp,
  FiUsers,
  FiWifi,
} from "react-icons/fi";

import { useHistory } from "react-router-dom";

import useAuth from "../../hooks/useAuth";
import useSwal from "../../hooks/useSwal";

import {
  resetAdminForgotPasswordService,
  sendAdminForgotPasswordOtpService,
  verifyAdminForgotPasswordOtpService,
} from "../../services/authService";


/*
|--------------------------------------------------------------------------
| FEATURES
|--------------------------------------------------------------------------
*/

const FEATURES = [
  {
    icon: FiPackage,
    title: "Inventory Management",
    text: "Manage products, categories, stock and pricing.",
  },
  {
    icon: FiBox,
    title: "Order Management",
    text: "Process customer orders through final delivery.",
  },
  {
    icon: FiUsers,
    title: "Customer Management",
    text: "Manage customer profiles, enquiries and service history.",
  },
  {
    icon: FiTool,
    title: "Repair & Service",
    text: "Track repairs, support requests and assignments.",
  },
];


/*
|--------------------------------------------------------------------------
| CHART PREVIEW
|--------------------------------------------------------------------------
*/

const CHART_BARS = [
  38,
  54,
  45,
  70,
  58,
  82,
  65,
  88,
  71,
  91,
  78,
  96,
];


/*
|--------------------------------------------------------------------------
| REMEMBERED BUSINESS CODE
|--------------------------------------------------------------------------
|
| Only the business code is remembered.
| Mobile number and password are never stored here.
|
| We save in:
| 1. localStorage
| 2. a non-sensitive browser cookie fallback
|
| The cookie fallback means the business code can survive even if the
| logout flow clears localStorage.
|
|--------------------------------------------------------------------------
*/

const BUSINESS_CODE_STORAGE_KEY =
  "comkwik_admin_business_code";

const BUSINESS_CODE_COOKIE_NAME =
  "comkwik_admin_business_code";


const getRememberedBusinessCode =
  () => {

    try {

      const localValue =
        String(
          window.localStorage.getItem(
            BUSINESS_CODE_STORAGE_KEY
          ) ||
          ""
        )
          .trim()
          .toUpperCase();


      if (
        localValue
      ) {

        return localValue;

      }

    } catch (
      error
    ) {

      console.warn(
        "Unable to read remembered business code from localStorage:",
        error
      );

    }


    try {

      const cookieParts =
        String(
          document.cookie ||
          ""
        )
          .split(";")
          .map(
            (
              item
            ) =>
              item.trim()
          );


      const cookieItem =
        cookieParts.find(
          (
            item
          ) =>
            item.startsWith(
              `${BUSINESS_CODE_COOKIE_NAME}=`
            )
        );


      if (
        cookieItem
      ) {

        return decodeURIComponent(
          cookieItem
            .slice(
              BUSINESS_CODE_COOKIE_NAME.length +
              1
            )
        )
          .trim()
          .toUpperCase();

      }

    } catch (
      error
    ) {

      console.warn(
        "Unable to read remembered business code from cookie:",
        error
      );

    }


    return "";

  };


const persistBusinessCode =
  (
    businessCode
  ) => {

    const normalizedBusinessCode =
      String(
        businessCode ||
        ""
      )
        .trim()
        .toUpperCase();


    if (
      !normalizedBusinessCode
    ) {

      return "";

    }


    try {

      window.localStorage.setItem(
        BUSINESS_CODE_STORAGE_KEY,
        normalizedBusinessCode
      );

    } catch (
      error
    ) {

      console.warn(
        "Unable to save remembered business code in localStorage:",
        error
      );

    }


    try {

      document.cookie =
        `${BUSINESS_CODE_COOKIE_NAME}=` +
        `${encodeURIComponent(normalizedBusinessCode)}; ` +
        "Max-Age=31536000; Path=/; SameSite=Lax";

    } catch (
      error
    ) {

      console.warn(
        "Unable to save remembered business code cookie:",
        error
      );

    }


    return normalizedBusinessCode;

  };


/*
|--------------------------------------------------------------------------
| LOGIN
|--------------------------------------------------------------------------
*/

const Login = () => {

  const history =
    useHistory();

  const {
    login,
  } =
    useAuth();

  const swal =
    useSwal();


  const tenantCodeInputRef =
    useRef(
      null
    );


  /*
  |--------------------------------------------------------------------------
  | FORM
  |--------------------------------------------------------------------------
  */

  const [
    form,
    setForm,
  ] =
    useState({
      tenantCode: "",
      mobile: "",
      password: "",
    });


  /*
  |--------------------------------------------------------------------------
  | ERRORS
  |--------------------------------------------------------------------------
  */

  const [
    rememberedBusinessCode,
    setRememberedBusinessCode,
  ] =
    useState(
      ""
    );


  const [
    businessCodeLocked,
    setBusinessCodeLocked,
  ] =
    useState(
      false
    );


  const [
    errors,
    setErrors,
  ] =
    useState({});


  /*
  |--------------------------------------------------------------------------
  | PASSWORD
  |--------------------------------------------------------------------------
  */

  const [
    showPassword,
    setShowPassword,
  ] =
    useState(
      false
    );


  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  const [
    loading,
    setLoading,
  ] =
    useState(
      false
    );


  const [
    forgotOpen,
    setForgotOpen,
  ] =
    useState(
      false
    );

  const [
    forgotStep,
    setForgotStep,
  ] =
    useState(
      "EMAIL"
    );

  const [
    forgotForm,
    setForgotForm,
  ] =
    useState({
      tenantCode: "",
      email: "",
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });

  const [
    forgotLoading,
    setForgotLoading,
  ] =
    useState(
      false
    );

  const [
    forgotError,
    setForgotError,
  ] =
    useState(
      ""
    );

  const [
    forgotMessage,
    setForgotMessage,
  ] =
    useState(
      ""
    );

  const [
    forgotResetToken,
    setForgotResetToken,
  ] =
    useState(
      ""
    );

  const [
    forgotResendSeconds,
    setForgotResendSeconds,
  ] =
    useState(
      0
    );

  const [
    showNewPassword,
    setShowNewPassword,
  ] =
    useState(
      false
    );

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] =
    useState(
      false
    );


  /*
  |--------------------------------------------------------------------------
  | LOAD REMEMBERED BUSINESS CODE
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      const storedBusinessCode =
        getRememberedBusinessCode();


      if (
        !storedBusinessCode
      ) {

        return;

      }


      setRememberedBusinessCode(
        storedBusinessCode
      );


      setBusinessCodeLocked(
        true
      );


      setForm(
        (
          previous
        ) => ({
          ...previous,

          tenantCode:
            storedBusinessCode,
        })
      );

    },
    []
  );


  useEffect(
    () => {

      if (
        forgotResendSeconds <=
        0
      ) {
        return undefined;
      }

      const timer =
        window.setInterval(
          () => {
            setForgotResendSeconds(
              (
                current
              ) =>
                Math.max(
                  0,
                  current - 1
                )
            );
          },
          1000
        );

      return () =>
        window.clearInterval(
          timer
        );

    },
    [
      forgotResendSeconds,
    ]
  );


  const openForgotPassword =
    () => {

      setForgotOpen(
        true
      );

      setForgotStep(
        "EMAIL"
      );

      setForgotForm({
        tenantCode:
          String(
            form.tenantCode ||
            rememberedBusinessCode ||
            ""
          )
            .trim()
            .toUpperCase(),

        email:
          "",

        otp:
          "",

        newPassword:
          "",

        confirmPassword:
          "",
      });

      setForgotError(
        ""
      );

      setForgotMessage(
        ""
      );

      setForgotResetToken(
        ""
      );

      setForgotResendSeconds(
        0
      );
    };


  const closeForgotPassword =
    () => {

      if (
        forgotLoading
      ) {
        return;
      }

      setForgotOpen(
        false
      );

      setForgotError(
        ""
      );

      setForgotMessage(
        ""
      );
    };


  const handleForgotChange =
    (
      event
    ) => {

      const {
        name,
        value,
      } =
        event.target;

      let nextValue =
        value;

      if (
        name ===
        "tenantCode"
      ) {
        nextValue =
          value
            .trimStart()
            .toUpperCase();
      }

      if (
        name ===
        "otp"
      ) {
        nextValue =
          value
            .replace(
              /\D/g,
              ""
            )
            .slice(
              0,
              6
            );
      }

      setForgotForm(
        (
          previous
        ) => ({
          ...previous,

          [name]:
            nextValue,
        })
      );

      setForgotError(
        ""
      );
    };


  const sendForgotOtp =
    async (
      isResend =
        false
    ) => {

      const tenantCode =
        String(
          forgotForm.tenantCode ||
          ""
        )
          .trim()
          .toUpperCase();

      const email =
        String(
          forgotForm.email ||
          ""
        )
          .trim()
          .toLowerCase();

      if (
        !tenantCode
      ) {
        setForgotError(
          "Business code is required."
        );
        return;
      }

      if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          email
        )
      ) {
        setForgotError(
          "Enter a valid registered email address."
        );
        return;
      }

      try {
        setForgotLoading(
          true
        );

        setForgotError(
          ""
        );

        setForgotMessage(
          ""
        );

        const response =
          await sendAdminForgotPasswordOtpService({
            tenantCode,
            email,
          });

        if (
          response?.success !==
            true &&
          response?.status !==
            "success"
        ) {
          throw new Error(
            response?.message ||
            "Unable to send OTP."
          );
        }

        setForgotForm(
          (
            previous
          ) => ({
            ...previous,
            tenantCode,
            email,
            otp:
              "",
          })
        );

        setForgotStep(
          "OTP"
        );

        setForgotResendSeconds(
          Number(
            response?.data
              ?.resendAfterSeconds ||
            60
          )
        );

        setForgotMessage(
          isResend
            ? "A new OTP has been sent to your registered email."
            : response?.message ||
              "OTP sent successfully."
        );
      } catch (
        error
      ) {
        setForgotError(
          error?.response?.data
            ?.message ||
          error?.message ||
          "Unable to send OTP."
        );
      } finally {
        setForgotLoading(
          false
        );
      }
    };


  const verifyForgotOtp =
    async (
      event
    ) => {

      event.preventDefault();

      const otp =
        String(
          forgotForm.otp ||
          ""
        )
          .replace(
            /\D/g,
            ""
          )
          .slice(
            0,
            6
          );

      if (
        otp.length !==
        6
      ) {
        setForgotError(
          "Enter the 6 digit OTP."
        );
        return;
      }

      try {
        setForgotLoading(
          true
        );

        setForgotError(
          ""
        );

        setForgotMessage(
          ""
        );

        const response =
          await verifyAdminForgotPasswordOtpService({
            tenantCode:
              forgotForm.tenantCode,

            email:
              forgotForm.email,

            otp,
          });

        if (
          response?.success !==
            true &&
          response?.status !==
            "success"
        ) {
          throw new Error(
            response?.message ||
            "Unable to verify OTP."
          );
        }

        setForgotResetToken(
          response?.data
            ?.resetToken ||
          ""
        );

        setForgotStep(
          "PASSWORD"
        );

        setForgotMessage(
          "Email verified. Create your new password."
        );
      } catch (
        error
      ) {
        setForgotError(
          error?.response?.data
            ?.message ||
          error?.message ||
          "Unable to verify OTP."
        );
      } finally {
        setForgotLoading(
          false
        );
      }
    };


  const resetForgotPassword =
    async (
      event
    ) => {

      event.preventDefault();

      if (
        String(
          forgotForm.newPassword ||
          ""
        ).length <
        8
      ) {
        setForgotError(
          "New password must be at least 8 characters."
        );
        return;
      }

      if (
        forgotForm.newPassword !==
        forgotForm.confirmPassword
      ) {
        setForgotError(
          "New password and confirm password do not match."
        );
        return;
      }

      try {
        setForgotLoading(
          true
        );

        setForgotError(
          ""
        );

        setForgotMessage(
          ""
        );

        const response =
          await resetAdminForgotPasswordService({
            tenantCode:
              forgotForm.tenantCode,

            resetToken:
              forgotResetToken,

            newPassword:
              forgotForm.newPassword,

            confirmPassword:
              forgotForm.confirmPassword,
          });

        if (
          response?.success !==
            true &&
          response?.status !==
            "success"
        ) {
          throw new Error(
            response?.message ||
            "Unable to reset password."
          );
        }

        setForgotStep(
          "SUCCESS"
        );

        setForgotMessage(
          response?.message ||
          "Password reset successfully."
        );

        setForm(
          (
            previous
          ) => ({
            ...previous,

            tenantCode:
              forgotForm.tenantCode,

            password:
              "",
          })
        );

        window.setTimeout(
          () => {
            setForgotOpen(
              false
            );
          },
          1800
        );
      } catch (
        error
      ) {
        setForgotError(
          error?.response?.data
            ?.message ||
          error?.message ||
          "Unable to reset password."
        );
      } finally {
        setForgotLoading(
          false
        );
      }
    };


  /*
  |--------------------------------------------------------------------------
  | BUSINESS CODE
  |--------------------------------------------------------------------------
  */

  const startBusinessCodeChange =
    () => {

      if (
        loading
      ) {

        return;

      }


      setBusinessCodeLocked(
        false
      );


      setErrors(
        (
          previous
        ) => ({
          ...previous,

          tenantCode:
            "",

          form:
            "",
        })
      );


      window.setTimeout(
        () => {

          tenantCodeInputRef
            .current
            ?.focus?.();


          tenantCodeInputRef
            .current
            ?.select?.();

        },
        0
      );

    };


  const cancelBusinessCodeChange =
    () => {

      if (
        loading ||
        !rememberedBusinessCode
      ) {

        return;

      }


      setForm(
        (
          previous
        ) => ({
          ...previous,

          tenantCode:
            rememberedBusinessCode,
        })
      );


      setBusinessCodeLocked(
        true
      );


      setErrors(
        (
          previous
        ) => ({
          ...previous,

          tenantCode:
            "",

          form:
            "",
        })
      );

    };


  const rememberBusinessCode =
    (
      businessCode
    ) => {

      const normalizedBusinessCode =
        persistBusinessCode(
          businessCode
        );


      if (
        !normalizedBusinessCode
      ) {

        return;

      }


      setRememberedBusinessCode(
        normalizedBusinessCode
      );


      setBusinessCodeLocked(
        true
      );


      setForm(
        (
          previous
        ) => ({
          ...previous,

          tenantCode:
            normalizedBusinessCode,
        })
      );

    };


  /*
  |--------------------------------------------------------------------------
  | HANDLE CHANGE
  |--------------------------------------------------------------------------
  */

  const handleChange = (
    event
  ) => {

    const {
      name,
      value,
    } =
      event.target;


    let nextValue =
      value;


    /*
    |--------------------------------------------------------------------------
    | BUSINESS CODE
    |--------------------------------------------------------------------------
    |
    | Store business code in uppercase so:
    |
    | comkwik
    | COMKWIK
    | ComKwik
    |
    | all become:
    |
    | COMKWIK
    |--------------------------------------------------------------------------
    */

    if (
      name ===
      "tenantCode"
    ) {

      if (
        businessCodeLocked
      ) {

        return;

      }


      nextValue =
        value
          .trimStart()
          .toUpperCase();

    }


    /*
    |--------------------------------------------------------------------------
    | MOBILE
    |--------------------------------------------------------------------------
    */

    if (
      name ===
      "mobile"
    ) {

      nextValue =
        value
          .replace(
            /\D/g,
            ""
          )
          .slice(
            0,
            10
          );

    }


    /*
    |--------------------------------------------------------------------------
    | UPDATE FORM
    |--------------------------------------------------------------------------
    */

    setForm(
      (
        previous
      ) => ({
        ...previous,

        [name]:
          nextValue,
      })
    );


    /*
    |--------------------------------------------------------------------------
    | CLEAR FIELD ERROR
    |--------------------------------------------------------------------------
    */

    setErrors(
      (
        previous
      ) => ({
        ...previous,

        [name]:
          "",

        form:
          "",
      })
    );

  };


  /*
  |--------------------------------------------------------------------------
  | VALIDATE
  |--------------------------------------------------------------------------
  */

  const validateForm = () => {

    const nextErrors =
      {};


    /*
    |--------------------------------------------------------------------------
    | BUSINESS CODE
    |--------------------------------------------------------------------------
    */

    if (
      !String(
        form.tenantCode ||
        ""
      ).trim()
    ) {

      nextErrors.tenantCode =
        "Business code is required.";

    }


    /*
    |--------------------------------------------------------------------------
    | MOBILE
    |--------------------------------------------------------------------------
    */

    if (
      !/^[6-9]\d{9}$/.test(
        form.mobile
      )
    ) {

      nextErrors.mobile =
        "Enter a valid 10-digit mobile number.";

    }


    /*
    |--------------------------------------------------------------------------
    | PASSWORD
    |--------------------------------------------------------------------------
    */

    if (
      !String(
        form.password ||
        ""
      ).trim()
    ) {

      nextErrors.password =
        "Password is required.";

    }


    setErrors(
      nextErrors
    );


    return (
      Object.keys(
        nextErrors
      ).length ===
      0
    );

  };


  /*
  |--------------------------------------------------------------------------
  | SUBMIT
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async (
      event
    ) => {

      event.preventDefault();


      /*
      |--------------------------------------------------------------------------
      | VALIDATION
      |--------------------------------------------------------------------------
      */

      if (
        !validateForm()
      ) {

        return;

      }


      try {

        setLoading(
          true
        );

        setErrors(
          {}
        );


        /*
        |--------------------------------------------------------------------------
        | TENANT LOGIN
        |--------------------------------------------------------------------------
        |
        | IMPORTANT:
        |
        | tenantCode is required by tenantLoginContext when using the
        | centralized admin portal / localhost.
        |
        | Request:
        |
        | {
        |   tenantCode,
        |   mobile,
        |   password
        | }
        |--------------------------------------------------------------------------
        */

        const response =
          await login({
            tenantCode:
              String(
                form.tenantCode ||
                ""
              )
                .trim()
                .toUpperCase(),

            mobile:
              form.mobile,

            password:
              form.password,
          });


        /*
        |--------------------------------------------------------------------------
        | SUCCESS
        |--------------------------------------------------------------------------
        */

        if (
          response?.success ||
          response?.status ===
            "success"
        ) {

          rememberBusinessCode(
            form.tenantCode
          );


          await swal.success({
            title:
              "Login Successful",

            text:
              response?.message ||
              "Welcome back to ComKwik Admin.",
          });


          history.replace(
            "/dashboard"
          );


          return;

        }


        /*
        |--------------------------------------------------------------------------
        | BACKEND ERROR
        |--------------------------------------------------------------------------
        */

        throw new Error(
          response?.message ||
          "Invalid business code, mobile number or password."
        );

      } catch (
        error
      ) {

        console.error(
          "Tenant admin login error:",
          error
        );


        const message =
          error?.response?.data
            ?.message ||
          error?.message ||
          "Unable to log in. Please try again.";


        setErrors({
          form:
            message,
        });


        swal.error({
          title:
            "Login Failed",

          text:
            message,
        });

      } finally {

        setLoading(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (

    <div className="comkwik-login-page">

      <div className="comkwik-login-background-grid" />


      <div className="comkwik-login-glow comkwik-login-glow-left" />


      <div className="comkwik-login-glow comkwik-login-glow-right" />


      <main className="comkwik-login-shell">

        {/*
        |--------------------------------------------------------------------------
        | BUSINESS PANEL
        |--------------------------------------------------------------------------
        */}

        <section className="comkwik-business-panel">

          {/*
          |--------------------------------------------------------------------------
          | HEADER
          |--------------------------------------------------------------------------
          */}

          <header className="comkwik-business-header">

            <div className="comkwik-brand">

              <div className="comkwik-brand-logo">

                <FiMonitor />

              </div>


              <div className="comkwik-brand-text">

                <span>
                  COMKWIK COMPUTERS
                </span>


                <strong>
                  Admin Portal
                </strong>

              </div>

            </div>


            <div className="comkwik-secure-badge">

              <FiShield />

              Secure Business Management

            </div>

          </header>


          {/*
          |--------------------------------------------------------------------------
          | HERO
          |--------------------------------------------------------------------------
          */}

          <section className="comkwik-business-hero">

            <div className="comkwik-business-hero-copy">

              <span className="comkwik-hero-label">

                COMPLETE BUSINESS CONTROL

              </span>


              <h1>

                Manage your complete
                business from
                one powerful dashboard.

              </h1>


              <p>

                Control inventory,
                online orders, customer
                enquiries, repair jobs
                and business performance
                from one secure
                administration system.

              </p>

            </div>


            <div className="comkwik-hero-summary">

              <div>

                <FiCheckCircle />

                Centralized system

              </div>


              <div>

                <FiCheckCircle />

                Real-time reporting

              </div>


              <div>

                <FiCheckCircle />

                Secure admin access

              </div>

            </div>

          </section>


          {/*
          |--------------------------------------------------------------------------
          | FEATURES
          |--------------------------------------------------------------------------
          */}

          <section className="comkwik-feature-grid">

            {FEATURES.map(
              (
                feature
              ) => {

                const Icon =
                  feature.icon;


                return (

                  <article
                    key={
                      feature.title
                    }
                    className="comkwik-feature-card"
                  >

                    <div className="comkwik-feature-icon">

                      <Icon />

                    </div>


                    <div className="comkwik-feature-content">

                      <strong>

                        {
                          feature.title
                        }

                      </strong>


                      <p>

                        {
                          feature.text
                        }

                      </p>

                    </div>


                    <span className="comkwik-feature-arrow">

                      →

                    </span>

                  </article>

                );

              }
            )}

          </section>


          {/*
          |--------------------------------------------------------------------------
          | DASHBOARD PREVIEW
          |--------------------------------------------------------------------------
          */}

          <section className="comkwik-dashboard-preview">

            <div className="comkwik-preview-header">

              <div>

                <span>
                  BUSINESS OVERVIEW
                </span>


                <strong>

                  Today&apos;s
                  Performance

                </strong>

              </div>


              <div className="comkwik-live-status">

                <span />

                Live Data

              </div>

            </div>


            <div className="comkwik-preview-body">

              {/*
              |--------------------------------------------------------------------------
              | SALES CHART
              |--------------------------------------------------------------------------
              */}

              <div className="comkwik-sales-chart">

                <div className="comkwik-chart-header">

                  <div>

                    <span>

                      Today&apos;s Sales

                    </span>


                    <strong>

                      ₹48,560

                    </strong>

                  </div>


                  <div className="comkwik-growth-badge">

                    <FiTrendingUp />

                    18.4%

                  </div>

                </div>


                <div className="comkwik-chart-area">

                  <div className="comkwik-chart-lines">

                    <span />

                    <span />

                    <span />

                  </div>


                  <div className="comkwik-chart-bars">

                    {CHART_BARS.map(
                      (
                        height,
                        index
                      ) => (

                        <span
                          key={`${height}-${index}`}
                          style={{
                            height:
                              `${height}%`,
                          }}
                        />

                      )
                    )}

                  </div>

                </div>


                <div className="comkwik-chart-labels">

                  <span>
                    9 AM
                  </span>

                  <span>
                    12 PM
                  </span>

                  <span>
                    3 PM
                  </span>

                  <span>
                    6 PM
                  </span>

                  <span>
                    9 PM
                  </span>

                </div>

              </div>


              {/*
              |--------------------------------------------------------------------------
              | BUSINESS METRICS
              |--------------------------------------------------------------------------
              */}

              <div className="comkwik-business-metrics">

                <article>

                  <div className="comkwik-metric-icon comkwik-metric-orders">

                    <FiBarChart2 />

                  </div>


                  <div>

                    <span>

                      Today&apos;s Orders

                    </span>


                    <strong>
                      28
                    </strong>


                    <small>

                      6 awaiting
                      confirmation

                    </small>

                  </div>

                </article>


                <article>

                  <div className="comkwik-metric-icon comkwik-metric-stock">

                    <FiPackage />

                  </div>


                  <div>

                    <span>

                      Low Stock Items

                    </span>


                    <strong>
                      12
                    </strong>


                    <small>

                      Reorder required

                    </small>

                  </div>

                </article>


                <article>

                  <div className="comkwik-metric-icon comkwik-metric-customers">

                    <FiUsers />

                  </div>


                  <div>

                    <span>

                      Total Customers

                    </span>


                    <strong>

                      1,248

                    </strong>


                    <small>

                      24 added this
                      month

                    </small>

                  </div>

                </article>

              </div>

            </div>

          </section>


          {/*
          |--------------------------------------------------------------------------
          | BOTTOM METRICS
          |--------------------------------------------------------------------------
          */}

          <section className="comkwik-bottom-metrics">

            <article>

              <FiClock />


              <div>

                <strong>
                  24/7
                </strong>


                <span>
                  Business access
                </span>

              </div>

            </article>


            <article>

              <FiShield />


              <div>

                <strong>
                  100%
                </strong>


                <span>
                  Secure control
                </span>

              </div>

            </article>


            <article>

              <FiDatabase />


              <div>

                <strong>
                  Real-time
                </strong>


                <span>
                  Data synchronization
                </span>

              </div>

            </article>


            <article>

              <FiActivity />


              <div>

                <strong>
                  One Portal
                </strong>


                <span>
                  Complete management
                </span>

              </div>

            </article>

          </section>


          {/*
          |--------------------------------------------------------------------------
          | BUSINESS FOOTER
          |--------------------------------------------------------------------------
          */}

          <footer className="comkwik-business-footer">

            <span>

              © 2026 ComKwik Computers

            </span>


            <span>

              RTech Capital
              Highstreet, Jagatpura,
              Jaipur

            </span>


            <span>

              Admin System v2.0

            </span>

          </footer>

        </section>


        {/*
        |--------------------------------------------------------------------------
        | AUTH PANEL
        |--------------------------------------------------------------------------
        */}

        <section className="comkwik-auth-panel">

          {/*
          |--------------------------------------------------------------------------
          | AUTH INTRO
          |--------------------------------------------------------------------------
          */}

          <header className="comkwik-auth-intro">

            <div className="comkwik-auth-intro-icon">

              <FiShield />

            </div>


            <div>

              <span>

                SECURE ADMIN ACCESS

              </span>


              <h2>

                Welcome to ComKwik

              </h2>


              <p>

                Access your business
                operations using your
                registered administrator
                account.

              </p>

            </div>

          </header>


          {/*
          |--------------------------------------------------------------------------
          | LOGIN CARD
          |--------------------------------------------------------------------------
          */}

          <div className="comkwik-login-card">

            {/*
            |--------------------------------------------------------------------------
            | MOBILE BRAND
            |--------------------------------------------------------------------------
            */}

            <div className="comkwik-mobile-brand">

              <div className="comkwik-brand-logo">

                <FiMonitor />

              </div>


              <div className="comkwik-brand-text">

                <span>

                  COMKWIK COMPUTERS

                </span>


                <strong>

                  Admin Portal

                </strong>

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | HEADING
            |--------------------------------------------------------------------------
            */}

            <div className="comkwik-card-heading">

              <div className="comkwik-card-heading-icon">

                <FiActivity />

              </div>


              <span>

                BUSINESS LOGIN

              </span>


              <h2>

                Welcome Back

              </h2>


              <p>

                Enter your business code,
                registered mobile number
                and password to continue.

              </p>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | FORM ERROR
            |--------------------------------------------------------------------------
            */}

            {errors.form ? (

              <div className="comkwik-login-error">

                <FiShield />


                <span>

                  {
                    errors.form
                  }

                </span>

              </div>

            ) : null}


            {/*
            |--------------------------------------------------------------------------
            | FORM
            |--------------------------------------------------------------------------
            */}

            <form
              className="comkwik-login-form"
              onSubmit={
                handleSubmit
              }
              noValidate
            >

              {/*
              |--------------------------------------------------------------------------
              | BUSINESS CODE
              |--------------------------------------------------------------------------
              */}

              <div className="comkwik-form-field">

                <label htmlFor="tenant-code">

                  Business Code

                </label>


                <div
                  className={`comkwik-input-wrapper ${
                    errors.tenantCode
                      ? "has-error"
                      : ""
                  } ${
                    businessCodeLocked
                      ? "is-business-code-locked"
                      : ""
                  }`}
                >

                  <span className="comkwik-input-icon">

                    <FiDatabase />

                  </span>


                  <input
                    ref={
                      tenantCodeInputRef
                    }
                    id="tenant-code"
                    type="text"
                    name="tenantCode"
                    value={
                      form.tenantCode
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Enter business code"
                    autoComplete="organization"
                    readOnly={
                      businessCodeLocked
                    }
                    disabled={
                      loading
                    }
                    aria-readonly={
                      businessCodeLocked
                    }
                  />


                  {rememberedBusinessCode ? (

                    businessCodeLocked ? (

                      <div className="comkwik-business-code-inline-actions">

                        <span
                          className="comkwik-business-code-lock"
                          title="Saved business code"
                          aria-hidden="true"
                        >

                          <FiLock />

                        </span>


                        <button
                          type="button"
                          className="comkwik-business-code-change"
                          onClick={
                            startBusinessCodeChange
                          }
                          disabled={
                            loading
                          }
                        >

                          Change

                        </button>

                      </div>

                    ) : (

                      <button
                        type="button"
                        className="comkwik-business-code-cancel"
                        onClick={
                          cancelBusinessCodeChange
                        }
                        disabled={
                          loading
                        }
                      >

                        Cancel

                      </button>

                    )

                  ) : null}

                </div>


                {errors.tenantCode ? (

                  <small className="comkwik-field-error">

                    {
                      errors.tenantCode
                    }

                  </small>

                ) : businessCodeLocked ? (

                  <small className="comkwik-field-help comkwik-business-code-saved">

                    <FiCheckCircle />

                    Saved on this device.

                  </small>

                ) : rememberedBusinessCode ? (

                  <small className="comkwik-field-help">

                    Enter another registered business code.

                  </small>

                ) : (

                  <small className="comkwik-field-help">

                    Enter your registered business code.
                    It will be remembered after a successful login.

                  </small>

                )}

              </div>


              {/*
              |--------------------------------------------------------------------------
              | MOBILE
              |--------------------------------------------------------------------------
              */}

              <div className="comkwik-form-field">

                <label htmlFor="admin-mobile">

                  Mobile Number

                </label>


                <div
                  className={`comkwik-input-wrapper ${
                    errors.mobile
                      ? "has-error"
                      : ""
                  }`}
                >

                  <span className="comkwik-input-icon">

                    <FiSmartphone />

                  </span>


                  <span className="comkwik-country-code">

                    +91

                  </span>


                  <input
                    id="admin-mobile"
                    type="tel"
                    name="mobile"
                    value={
                      form.mobile
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Enter mobile number"
                    inputMode="numeric"
                    autoComplete="username"
                    maxLength={
                      10
                    }
                    disabled={
                      loading
                    }
                  />

                </div>


                {errors.mobile ? (

                  <small className="comkwik-field-error">

                    {
                      errors.mobile
                    }

                  </small>

                ) : (

                  <small className="comkwik-field-help">

                    Use your registered
                    administrator mobile
                    number.

                  </small>

                )}

              </div>


              {/*
              |--------------------------------------------------------------------------
              | PASSWORD
              |--------------------------------------------------------------------------
              */}

              <div className="comkwik-form-field">

                <label htmlFor="admin-password">

                  Password

                </label>


                <div
                  className={`comkwik-input-wrapper ${
                    errors.password
                      ? "has-error"
                      : ""
                  }`}
                >

                  <span className="comkwik-input-icon">

                    <FiLock />

                  </span>


                  <input
                    id="admin-password"
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    name="password"
                    value={
                      form.password
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Enter password"
                    autoComplete="current-password"
                    disabled={
                      loading
                    }
                  />


                  <button
                    type="button"
                    className="comkwik-password-toggle"
                    onClick={() =>
                      setShowPassword(
                        (
                          previous
                        ) =>
                          !previous
                      )
                    }
                    disabled={
                      loading
                    }
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >

                    {showPassword ? (

                      <FiEyeOff />

                    ) : (

                      <FiEye />

                    )}

                  </button>

                </div>


                {errors.password ? (

                  <small className="comkwik-field-error">

                    {
                      errors.password
                    }

                  </small>

                ) : null}

              </div>


              <div className="comkwik-forgot-password-row">

                <button
                  type="button"
                  className="comkwik-forgot-password-link"
                  onClick={
                    openForgotPassword
                  }
                  disabled={
                    loading
                  }
                >
                  Forgot password?
                </button>

              </div>


              {/*
              |--------------------------------------------------------------------------
              | SECURITY
              |--------------------------------------------------------------------------
              */}

              <div className="comkwik-login-protection">

                <FiCheck />


                <span>

                  Protected with secure
                  authentication and
                  encrypted communication.

                </span>

              </div>


              {/*
              |--------------------------------------------------------------------------
              | SUBMIT
              |--------------------------------------------------------------------------
              */}

              <button
                type="submit"
                className="comkwik-login-button"
                disabled={
                  loading
                }
              >

                {loading ? (

                  <>

                    <span className="comkwik-login-spinner" />

                    Signing In...

                  </>

                ) : (

                  <>

                    Login to Dashboard

                    <FiBarChart2 />

                  </>

                )}

              </button>

            </form>


            {/*
            |--------------------------------------------------------------------------
            | CARD FOOTER
            |--------------------------------------------------------------------------
            */}

            <div className="comkwik-card-footer">

              <FiShield />


              <span>

                Authorized
                administrators only

              </span>

            </div>

          </div>


          {/*
          |--------------------------------------------------------------------------
          | AUTH INFORMATION
          |--------------------------------------------------------------------------
          */}

          <section className="comkwik-auth-information">

            <article>

              <div className="comkwik-auth-info-icon comkwik-auth-info-secure">

                <FiLock />

              </div>


              <div>

                <strong>

                  Secure Connection

                </strong>


                <span>

                  Credentials are
                  transmitted securely.

                </span>

              </div>

            </article>


            <article>

              <div className="comkwik-auth-info-icon comkwik-auth-info-server">

                <FiServer />

              </div>


              <div>

                <strong>

                  System Online

                </strong>


                <span>

                  Admin services are
                  operating normally.

                </span>

              </div>

            </article>


            <article>

              <div className="comkwik-auth-info-icon comkwik-auth-info-support">

                <FiWifi />

              </div>


              <div>

                <strong>

                  Need Assistance?

                </strong>


                <span>

                  Contact the system
                  administrator for
                  support.

                </span>

              </div>

            </article>

          </section>


          {/*
          |--------------------------------------------------------------------------
          | AUTH FOOTER
          |--------------------------------------------------------------------------
          */}

          <footer className="comkwik-auth-footer">

            <span>

              Secure Business
              Administration

            </span>


            <span>

              ComKwik Computers

            </span>

          </footer>

        </section>

      </main>


      {forgotOpen ? (

        <div className="comkwik-forgot-overlay">

          <section
            className="comkwik-forgot-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="comkwik-forgot-title"
          >

            <button
              type="button"
              className="comkwik-forgot-close"
              onClick={
                closeForgotPassword
              }
              disabled={
                forgotLoading
              }
              aria-label="Close forgot password"
            >
              ×
            </button>


            <div className="comkwik-forgot-icon">
              {forgotStep ===
              "EMAIL" ? (
                <FiMail />
              ) : forgotStep ===
                "OTP" ? (
                <FiKey />
              ) : (
                <FiLock />
              )}
            </div>


            <header className="comkwik-forgot-header">
              <span>
                SECURE ACCOUNT RECOVERY
              </span>

              <h2 id="comkwik-forgot-title">
                {forgotStep ===
                "EMAIL"
                  ? "Forgot Password"
                  : forgotStep ===
                    "OTP"
                    ? "Verify Email OTP"
                    : forgotStep ===
                      "PASSWORD"
                      ? "Create New Password"
                      : "Password Updated"}
              </h2>

              <p>
                {forgotStep ===
                "EMAIL"
                  ? "Enter your business code and registered administrator email."
                  : forgotStep ===
                    "OTP"
                    ? `Enter the 6 digit OTP sent to ${forgotForm.email}.`
                    : forgotStep ===
                      "PASSWORD"
                      ? "Choose a new password for your administrator account."
                      : "Your password has been reset. You can now login with the new password."}
              </p>
            </header>


            {forgotError ? (
              <div className="comkwik-forgot-error">
                <FiShield />
                <span>
                  {forgotError}
                </span>
              </div>
            ) : null}


            {forgotMessage ? (
              <div className="comkwik-forgot-success">
                <FiCheckCircle />
                <span>
                  {forgotMessage}
                </span>
              </div>
            ) : null}


            {forgotStep ===
            "EMAIL" ? (
              <form
                className="comkwik-forgot-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  sendForgotOtp(false);
                }}
                noValidate
              >
                <div className="comkwik-forgot-field">
                  <label htmlFor="forgot-tenant-code">
                    Business Code
                  </label>

                  <div className="comkwik-forgot-input">
                    <FiDatabase />

                    <input
                      id="forgot-tenant-code"
                      name="tenantCode"
                      value={
                        forgotForm.tenantCode
                      }
                      onChange={
                        handleForgotChange
                      }
                      placeholder="COMKWIK"
                      autoComplete="organization"
                      disabled={
                        forgotLoading
                      }
                    />
                  </div>
                </div>


                <div className="comkwik-forgot-field">
                  <label htmlFor="forgot-email">
                    Registered Email
                  </label>

                  <div className="comkwik-forgot-input">
                    <FiMail />

                    <input
                      id="forgot-email"
                      type="email"
                      name="email"
                      value={
                        forgotForm.email
                      }
                      onChange={
                        handleForgotChange
                      }
                      placeholder="admin@business.com"
                      autoComplete="email"
                      disabled={
                        forgotLoading
                      }
                    />
                  </div>
                </div>


                <button
                  type="submit"
                  className="comkwik-forgot-primary"
                  disabled={
                    forgotLoading
                  }
                >
                  {forgotLoading
                    ? "Sending OTP..."
                    : "Send Email OTP"}
                </button>
              </form>
            ) : null}


            {forgotStep ===
            "OTP" ? (
              <form
                className="comkwik-forgot-form"
                onSubmit={
                  verifyForgotOtp
                }
                noValidate
              >
                <div className="comkwik-forgot-field">
                  <label htmlFor="forgot-otp">
                    6 Digit OTP
                  </label>

                  <div className="comkwik-forgot-input">
                    <FiKey />

                    <input
                      id="forgot-otp"
                      type="text"
                      name="otp"
                      value={
                        forgotForm.otp
                      }
                      onChange={
                        handleForgotChange
                      }
                      placeholder="000000"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={
                        6
                      }
                      className="comkwik-forgot-otp-input"
                      disabled={
                        forgotLoading
                      }
                    />
                  </div>
                </div>


                <button
                  type="submit"
                  className="comkwik-forgot-primary"
                  disabled={
                    forgotLoading
                  }
                >
                  {forgotLoading
                    ? "Verifying..."
                    : "Verify OTP"}
                </button>


                <button
                  type="button"
                  className="comkwik-forgot-secondary"
                  onClick={() =>
                    sendForgotOtp(
                      true
                    )
                  }
                  disabled={
                    forgotLoading ||
                    forgotResendSeconds >
                      0
                  }
                >
                  {forgotResendSeconds >
                  0
                    ? `Resend OTP in ${forgotResendSeconds}s`
                    : "Resend OTP"}
                </button>


                <button
                  type="button"
                  className="comkwik-forgot-back"
                  onClick={() => {
                    setForgotStep(
                      "EMAIL"
                    );
                    setForgotError(
                      ""
                    );
                    setForgotMessage(
                      ""
                    );
                  }}
                  disabled={
                    forgotLoading
                  }
                >
                  <FiArrowLeft />
                  Change Email
                </button>
              </form>
            ) : null}


            {forgotStep ===
            "PASSWORD" ? (
              <form
                className="comkwik-forgot-form"
                onSubmit={
                  resetForgotPassword
                }
                noValidate
              >
                <div className="comkwik-forgot-field">
                  <label htmlFor="forgot-new-password">
                    New Password
                  </label>

                  <div className="comkwik-forgot-input">
                    <FiLock />

                    <input
                      id="forgot-new-password"
                      type={
                        showNewPassword
                          ? "text"
                          : "password"
                      }
                      name="newPassword"
                      value={
                        forgotForm.newPassword
                      }
                      onChange={
                        handleForgotChange
                      }
                      placeholder="Minimum 8 characters"
                      autoComplete="new-password"
                      disabled={
                        forgotLoading
                      }
                    />

                    <button
                      type="button"
                      className="comkwik-forgot-password-toggle"
                      onClick={() =>
                        setShowNewPassword(
                          (
                            current
                          ) =>
                            !current
                        )
                      }
                    >
                      {showNewPassword
                        ? <FiEyeOff />
                        : <FiEye />}
                    </button>
                  </div>
                </div>


                <div className="comkwik-forgot-field">
                  <label htmlFor="forgot-confirm-password">
                    Confirm Password
                  </label>

                  <div className="comkwik-forgot-input">
                    <FiLock />

                    <input
                      id="forgot-confirm-password"
                      type={
                        showConfirmPassword
                          ? "text"
                          : "password"
                      }
                      name="confirmPassword"
                      value={
                        forgotForm.confirmPassword
                      }
                      onChange={
                        handleForgotChange
                      }
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                      disabled={
                        forgotLoading
                      }
                    />

                    <button
                      type="button"
                      className="comkwik-forgot-password-toggle"
                      onClick={() =>
                        setShowConfirmPassword(
                          (
                            current
                          ) =>
                            !current
                        )
                      }
                    >
                      {showConfirmPassword
                        ? <FiEyeOff />
                        : <FiEye />}
                    </button>
                  </div>
                </div>


                <button
                  type="submit"
                  className="comkwik-forgot-primary"
                  disabled={
                    forgotLoading
                  }
                >
                  {forgotLoading
                    ? "Updating Password..."
                    : "Reset Password"}
                </button>
              </form>
            ) : null}


            {forgotStep ===
            "SUCCESS" ? (
              <div className="comkwik-forgot-success-panel">
                <FiCheckCircle />
                <strong>
                  Password reset successful
                </strong>
                <span>
                  Returning to login...
                </span>
              </div>
            ) : null}

          </section>

        </div>

      ) : null}


    </div>

  );

};


export default Login;